import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent } from '@/components/ui/popover'
import { UserAvatar } from '@/components/UserProfileCard'
import { useUserList } from '@/features/user/hooks'
import type { User } from '@/schema'
import { cn, formatFullName } from '@/utils'
import { DEFAULT_GET_ALL_LIMIT, SortDirection } from '@/utils/constants'

interface MentionPopoverProps {
  searchQuery: string
  position: { top: number; left: number } | null
  onSelect: (user: User) => void
  onClose: () => void
}

export const MentionPopover: React.FC<MentionPopoverProps> = ({
  searchQuery,
  position,
  onSelect,
  onClose,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [popoverHeight, setPopoverHeight] = useState<number | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const popoverContentRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  // Fetch users - only enabled when position exists
  // Filtering is done on API side via search parameter
  const { data: { data: users = [] } = {}, isLoading } = useUserList({
    search: searchQuery,
    sort: { column: 'email', direction: SortDirection.ASC },
    enabled: position !== null, // Only enable when position exists
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
  })

  // Measure actual popover height after render
  useEffect(() => {
    if (popoverContentRef.current) {
      const height = popoverContentRef.current.offsetHeight
      setPopoverHeight(height)
    }
  }, [users, isLoading, searchQuery])

  // Calculate if popover should be positioned above or below
  // Recalculate when position or popoverHeight changes
  const popoverPosition = useMemo(() => {
    if (!position) return { top: 0, left: 0, side: 'bottom' as const }

    const viewportHeight = window.innerHeight
    const availableSpaceBelow = viewportHeight - position.top
    // Use actual height if available, otherwise fallback to max height (300px)
    const actualPopoverHeight = popoverHeight ?? 300
    const minSpaceRequired = actualPopoverHeight + 20 // Add some padding for spacing

    // If not enough space below, position above
    if (availableSpaceBelow < minSpaceRequired) {
      // Position above the cursor
      // Calculate top position: cursor position - actual popover height - gap
      const gap = 5 // 5px gap above cursor
      const topPosition = position.top - actualPopoverHeight - gap

      // Ensure popover doesn't go above viewport (with 10px margin from top)
      const minTop = 10
      const adjustedTop = Math.max(topPosition, minTop)

      return {
        top: adjustedTop - 20, // 20px margin from top
        left: position.left,
        side: 'top' as const,
      }
    }

    return {
      top: position.top + 5, // 5px gap below cursor
      left: position.left,
      side: 'bottom' as const,
    }
  }, [position, popoverHeight])

  // Reset selected index when filtered users change
  useEffect(() => {
    setSelectedIndex(0)
  }, [users])

  // Scroll selected item into view
  useEffect(() => {
    if (users.length > 0 && listRef.current) {
      const selectedUser = users[selectedIndex]
      if (selectedUser?.id) {
        const itemElement = itemRefs.current.get(selectedUser.id)
        if (itemElement) {
          itemElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      }
    }
  }, [selectedIndex, users])

  const handleSelect = useCallback(
    (user: User) => {
      onSelect(user)
      onClose()
    },
    [onSelect, onClose]
  )

  // Handle mouse wheel events to ensure scrolling works properly
  // This prevents event propagation issues that might block scrolling
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    // Allow the scroll container to handle wheel events naturally
    // Stop propagation to prevent parent elements from interfering
    e.stopPropagation()
  }, [])

  if (!position) return null

  // Show loading state while fetching initial data
  if (isLoading) {
    return (
      <Popover open>
        <PopoverContent
          ref={popoverContentRef}
          style={{
            position: 'fixed',
            top: popoverPosition.top,
            left: popoverPosition.left,
            zIndex: 1000,
          }}
          side={popoverPosition.side}
          className={cn(
            'min-w-50 max-w-[300px] max-h-[300px]',
            'overflow-hidden p-0'
          )}
        >
          <div className="p-3 text-sm text-gray-500">Loading users...</div>
        </PopoverContent>
      </Popover>
    )
  }

  // Show empty state only after data is loaded
  if (!isLoading && users.length === 0) {
    return (
      <Popover open>
        <PopoverContent
          ref={popoverContentRef}
          style={{
            position: 'fixed',
            top: popoverPosition.top,
            left: popoverPosition.left,
            zIndex: 1000,
          }}
          side={popoverPosition.side}
          className={cn(
            'min-w-50 max-w-[300px] max-h-[300px]',
            'overflow-hidden p-0'
          )}
        >
          <div className="p-3 text-sm text-gray-500">
            {searchQuery.trim() ? 'No users found' : 'Type to search users...'}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover open>
      <PopoverContent
        ref={popoverContentRef}
        style={{
          position: 'fixed',
          top: popoverPosition.top,
          left: popoverPosition.left,
          zIndex: 1000,
        }}
        side={popoverPosition.side}
        className={cn(
          'min-w-50 max-w-[300px] max-h-[300px]',
          'overflow-y-auto p-0'
        )}
        onWheel={handleWheel}
      >
        <div ref={listRef} className="py-1">
          {users.map((user: User) => {
            if (!user.id) return null

            const fullName = formatFullName(user.first_name, user.last_name)
            return (
              <Button
                key={user.id}
                variant="text"
                color="default"
                fullWidth
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(user.id, el)
                  } else {
                    itemRefs.current.delete(user.id)
                  }
                }}
                onClick={() => handleSelect(user)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleSelect(user)
                  }
                }}
                className={cn(
                  'justify-start text-left px-3 py-2 text-sm rounded-none border-none',
                  'hover:bg-blue-50 transition-colors'
                )}
                leadingIcon={
                  <UserAvatar
                    avatarUrl={user.avatar_url || ''}
                    userName={fullName}
                    className="size-7"
                    textClassName="text-xs"
                  />
                }
              >
                <div className="flex flex-col">
                  <div className="text-sm font-medium leading-5">
                    {fullName}
                  </div>
                  <div className="text-xs leading-4 text-gray-500">
                    {user.email || '-'}
                  </div>
                </div>
              </Button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
